import { Text } from '@/components/ui/text'
import { api } from '@api'
import type { Tables } from '@hvf/contracts'
import { useQuery } from '@tanstack/solid-query'
import { Star } from 'lucide-solid'
import { Show } from 'solid-js'
import { Flex } from 'styled-system/jsx'

type CommunityScoreResult = { score: number; count: number }

async function fetchCommunityScore(setupId: string): Promise<CommunityScoreResult> {
  const { data: commanders, error: commandersErr } = await api()
    .from('profile_private')
    .select('id')
    .eq('role', 'commander')

  if (commandersErr) throw commandersErr

  const commanderIds = (commanders ?? []).map((r) => r.id)

  let query = api()
    .from('taken_setups')
    .select('score')
    .eq('setup_id', setupId)
    .not('score', 'is', null)

  if (commanderIds.length > 0) {
    query = query.not('user_id', 'in', `(${commanderIds.join(',')})`)
  }

  const { data, error } = await query

  if (error) throw error

  const scores = (data ?? []).map((r) => r.score).filter((s): s is number => typeof s === 'number')
  if (scores.length === 0) return { score: 0, count: 0 }

  return {
    score: scores.reduce((sum, s) => sum + s, 0) / scores.length,
    count: scores.length,
  }
}

export const CommunityScore = (props: { setup: Tables<'setup'> | undefined }) => {

  const q = useQuery(() => ({
    queryKey: ['community-score-count', props.setup?.id],

    // Safe: 'enabled' prevents this from running without an ID.
    queryFn: () => fetchCommunityScore(props.setup?.id || ''),

    enabled: !!props.setup?.id,

    staleTime: 30 * 60 * 1000, // 30m cache freshness
    gcTime: 30 * 60 * 1000, // 30m cache retention
    retry: 1,
    placeholderData: { score: 0, count: 0 },
  }))

  const score = () => {
    const val = q.data?.score ?? 0
    return Number.isInteger(val) ? val : val.toFixed(1)
  }

  return (
    <Show
      when={(q.data?.count ?? 0) > 0}
      fallback={<Text fontSize='14.5px' fontWeight='700' color='editorialInk3'>-</Text>}
    >
      <Flex alignItems='center' gap='4px'>
        <Text fontSize='13px' fontWeight='600' color='editorialInk3'>({q.data?.count})</Text>
        <Star size={15} fill='var(--colors-editorial-star)' stroke='var(--colors-editorial-star)' />
        <Text fontSize='14.5px' fontWeight='700' color='editorialInk'>{score()}</Text>
      </Flex>
    </Show>
  )
}
